/* eslint-disable react/prop-types */
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonBadge from "components/ArgonBadge";
import ArgonTypography from "components/ArgonTypography";
import ArgonProgress from "components/ArgonProgress";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Link } from "react-router-dom";

const formatDate = (value) => {
  if (!value) {
    return "N/A";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getTutorInfo = (attendance, group) => {
  const tutorCandidate = attendance?.tutor || group.tutor;
  if (tutorCandidate) {
    return {
      name: tutorCandidate.name || tutorCandidate.fullName || "N/A",
      email: tutorCandidate.email || "",
    };
  }

  const sessionTutor = (group.classSession?.tutors || [])[0];
  if (sessionTutor) {
    return {
      name: sessionTutor.name || sessionTutor.fullName || "N/A",
      email: sessionTutor.email || "",
    };
  }

  return { name: "N/A", email: "" };
};

const getStudentsLabel = (students = []) => {
  if (!students.length) {
    return { studentName: "N/A", parentName: "" };
  }

  if (students.length === 1) {
    const student = students[0];
    const studentName = student.name || "N/A";
    const parentName = student.parentId?.name || student.parentId?.fullName || "";
    return { studentName, parentName };
  }

  return { studentName: `${students.length} Students`, parentName: "" };
};

const calculateProgress = (group) => {
  const progress = group.classProgress || {};
  const completedFromProgress = progress.completedClasses ?? group.attendances.length;
  const totalFromProgress =
    progress.totalClasses ?? Math.max(completedFromProgress, group.attendances.length);
  const remainingFromProgress =
    progress.remainingClasses ?? Math.max(0, totalFromProgress - completedFromProgress);

  return {
    totalClasses: totalFromProgress,
    completedClasses: completedFromProgress,
    remainingClasses: remainingFromProgress,
  };
};

const getStatusColor = (status) => {
  switch (status) {
    case "COMPLETED":
      return "success";
    case "ONGOING":
    case "SCHEDULED":
    case "ACTIVE":
      return "info";
    case "ON_HOLD":
      return "warning";
    case "CANCELLED":
      return "error";
    default:
      return "secondary";
  }
};

const getStatusDisplayName = (status) => {
  // Map backend status values to frontend display names
  if (status === "SCHEDULED") {
    return "ACTIVE";
  }
  return status;
};

const getPaymentColor = (paymentStatus) => {
  switch (paymentStatus) {
    case "COMPLETED":
      return "success";
    case "OVERDUE":
    case "FAILED":
      return "error";
    case "IN_PROGRESS":
      return "info";
    default:
      return "warning";
  }
};

function RemainingProgress({ percentage }) {
  const normalizedPercentage = Math.min(100, Math.max(0, Number(percentage) || 0));
  const hasProgress = normalizedPercentage > 0;

  return (
    <ArgonBox position="relative" width="120px" height="25px">
      <ArgonBox
        width="100%"
        height="100%"
        bgColor="#E9ECEF"
        borderRadius="4px"
        position="relative"
        overflow="hidden"
        sx={{
          boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1)",
          border: "1px solid #dee2e6",
        }}
      >
        {hasProgress && (
          <ArgonBox
            width={`${normalizedPercentage}%`}
            height="100%"
            bgColor="primary"
            borderRadius="4px"
            position="absolute"
            top={0}
            left={0}
            sx={{
              background: "linear-gradient(135deg, #6477e2ff 0%, #4c63d2 100%)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2), 0 1px 2px rgba(0,0,0,0.2)",
              border: "1px solid #3b4cb8",
            }}
          />
        )}
        <ArgonBox
          position="absolute"
          top="50%"
          left="50%"
          sx={{ transform: "translate(-50%, -50%)" }}
        >
          <ArgonTypography
            variant="caption"
            fontWeight="bold"
            color={hasProgress ? "white" : "text"}
            sx={{
              textShadow: hasProgress ? "0 1px 2px rgba(0,0,0,0.3)" : "none",
              fontSize: "11px",
            }}
          >
            {normalizedPercentage}%
          </ArgonTypography>
        </ArgonBox>
      </ArgonBox>
    </ArgonBox>
  );
}

const AttendanceTableData = (handleDeleteClick, handleEditClick, attendanceData = []) => {
  const groupedSessions = attendanceData.reduce((accumulator, attendance) => {
    const session = attendance.classSession || {};
    const sessionId =
      session.id || session._id || attendance.classSessionId || attendance.classSessionId?._id;

    if (!sessionId) {
      return accumulator;
    }

    if (!accumulator[sessionId]) {
      accumulator[sessionId] = {
        classSession: session,
        attendances: [],
        classProgress: attendance.classProgress || null,
        tutor: attendance.tutor || null,
      };
    }

    accumulator[sessionId].attendances.push(attendance);

    if (!accumulator[sessionId].classProgress && attendance.classProgress) {
      accumulator[sessionId].classProgress = attendance.classProgress;
    }

    if (!accumulator[sessionId].tutor && attendance.tutor) {
      accumulator[sessionId].tutor = attendance.tutor;
    }

    return accumulator;
  }, {});

  const rows = Object.values(groupedSessions).map((group, index) => {
    const latestAttendance = group.attendances.reduce((latest, current) => {
      if (!current.attendanceTime) {
        return latest;
      }

      if (!latest) {
        return current;
      }

      return new Date(current.attendanceTime) > new Date(latest.attendanceTime) ? current : latest;
    }, null);

    const topicCovered = latestAttendance?.topicCovered || "Class Session";
    const attendanceDate = formatDate(latestAttendance?.attendanceTime);
    const classSession = group.classSession || {};

    // Get subject names from classSession
    const subjectNames =
      classSession.subject || classSession.subjectName || latestAttendance?.subject || "";

    // Get class name from classSession or students' class
    const rawClassName =
      classSession.className ||
      classSession.name ||
      (classSession.students && classSession.students[0]?.class?.name) ||
      (classSession.students && classSession.students[0]?.class?.className) ||
      "";

    // Combine subject and class name
    let displayClassName = "";
    if (subjectNames && rawClassName) {
      displayClassName = `${subjectNames} - ${rawClassName}`;
    } else if (subjectNames) {
      displayClassName = subjectNames;
    } else if (rawClassName) {
      displayClassName = rawClassName;
    } else {
      displayClassName = topicCovered || "Class Session";
    }

    const tutorInfo = getTutorInfo(latestAttendance, group);
    const students = classSession.students || [];

    const studentInfo = getStudentsLabel(students);
    const progress = calculateProgress(group);
    const remainingPercentage =
      progress.totalClasses > 0
        ? Math.round((progress.remainingClasses / progress.totalClasses) * 100)
        : 0;

    // Determine status based on attendance type
    let status = classSession.status || "CREATED";
    // If the latest attendance type is ABSENT (case-insensitive), override status to ON_HOLD
    const attendanceType = latestAttendance?.type
      ? String(latestAttendance.type).toUpperCase()
      : "";
    if (attendanceType === "ABSENT") {
      status = "ON_HOLD";
    }

    const paymentStatus = classSession.paymentStatus || "PENDING";
    const sessionId = classSession.id || classSession._id || null;
    const mode = classSession.mode || "N/A";

    // Find leaveFor from any attendance that has it set, but only show if status is ON_HOLD
    const attendanceWithLeave = group.attendances.find((att) => att.leaveFor);
    const leaveFor = status === "ON_HOLD" ? attendanceWithLeave?.leaveFor || "N/A" : "N/A";

    return {
      sno: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {index + 1}
        </ArgonTypography>
      ),
      Class: (
        <ArgonTypography
          variant="caption"
          component={sessionId ? Link : "span"}
          to={sessionId ? `/attendance/profile/${sessionId}` : undefined}
          color={sessionId ? "primary" : "text"}
          fontWeight="medium"
          sx={{ pointerEvents: sessionId ? "auto" : "none" }}
        >
          {displayClassName}
        </ArgonTypography>
      ),
      // "Tutor Name": (
      //   <ArgonBox display="flex" flexDirection="column">
      //     <ArgonTypography variant="caption" fontWeight="medium" color="text">
      //       {tutorInfo.name}
      //     </ArgonTypography>
      //     {tutorInfo.email && (
      //       <ArgonTypography variant="caption" color="secondary">
      //         {tutorInfo.email}
      //       </ArgonTypography>
      //     )}
      //   </ArgonBox>
      // ),
      Type: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {latestAttendance?.type || classSession.type || "N/A"}
        </ArgonTypography>
      ),
      Student: (
        <ArgonBox display="flex" flexDirection="column">
          <ArgonTypography variant="caption" fontWeight="medium" color="text">
            {studentInfo.studentName}
          </ArgonTypography>
          {studentInfo.parentName && (
            <ArgonTypography variant="caption" color="secondary">
              Client - {studentInfo.parentName}
            </ArgonTypography>
          )}
        </ArgonBox>
      ),
      Mode: (
        <ArgonBadge
          variant="contained"
          badgeContent={mode}
          color={mode === "ONLINE" ? "info" : mode === "OFFLINE" ? "success" : "primary"}
          size="xs"
          container
        />
      ),
      "Remaining class": <RemainingProgress percentage={remainingPercentage} />,
      "Duration left": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {progress.remainingClasses} hours
        </ArgonTypography>
      ),
      "class status": (
        <ArgonBadge
          variant="gradient"
          badgeContent={getStatusDisplayName(status)}
          color={getStatusColor(status)}
          size="xs"
          container
        />
      ),
      "Leave By": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {leaveFor}
        </ArgonTypography>
      ),
      "cycle payment status": (
        <ArgonBadge
          variant="gradient"
          badgeContent={paymentStatus}
          color={getPaymentColor(paymentStatus)}
          size="xs"
          container
        />
      ),
      "Last Attendance Date": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {attendanceDate}
        </ArgonTypography>
      ),
      Action: (
        <ArgonBox display="flex" alignItems="center" gap={0}>
          <ArgonButton
            variant="text"
            size="large"
            component={sessionId ? Link : "button"}
            to={sessionId ? `/attendance/profile/${sessionId}` : undefined}
            sx={{ minWidth: "12px", p: 0 }}
            disabled={!sessionId}
          >
            <VisibilityIcon color="primary" />
          </ArgonButton>
          {/* <ArgonButton
            variant="text"
            size="large"
            sx={{ minWidth: "32px", p: 0 }}
            onClick={() => handleEditClick && handleEditClick(latestAttendance)}
            disabled={!latestAttendance || !handleEditClick}
          >
            <EditIcon color="info" />
          </ArgonButton>
          <ArgonButton
            variant="text"
            size="large"
            sx={{ minWidth: "32px", p: 0 }}
            onClick={() => handleDeleteClick(sessionId)}
          >
            <DeleteIcon color="error" />
          </ArgonButton> */}
        </ArgonBox>
      ),
    };
  });

  return {
    columns: [
      { name: "sno", align: "center" },
      { name: "Class", align: "left" },
      // { name: "Tutor Name", align: "left" },
      { name: "Type", align: "left" },
      { name: "Student", align: "left" },
      { name: "Mode", align: "center" },
      { name: "Remaining class", align: "left" },
      { name: "Duration left", align: "left" },
      { name: "class status", align: "center" },
      { name: "Leave By", align: "center" },
      { name: "cycle payment status", align: "center" },
      { name: "Last Attendance Date", align: "center" },
      { name: "Action", align: "center" },
    ],
    rows,
  };
};

export default AttendanceTableData;
